'use strict';

/*
	Device Support 
	ios mobile device - supports vimeo display inline (native player via prop to open in fullscreen on play)
	android mobile device - supports vimeo display inline or the native html5 fullscreen api
	ipad - vimeo display inline param doesn't work or has the full screen api (vimeo makes it use the ios video player but on fullscreen press which we have no access to)
	android tablet - vimeo display inline param doesn't work but supports the full screen api
	Desktop - supports fullscreen and works fine everywhere

	Special Cases 
	- Code Page doesn't use the display inline property on mobile and uses our own fullscreen view
	- Video Modal on ipad has the fullscreen button removed 

	Note
	- Native Video doesn't seem to support our external volume support which is why its currently hidden
*/

import _ from 'lodash';

import './VideoPlayer.less';

import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import Vimeo from '@vimeo/player';
import { ProgressBar, TimeMarker } from 'react-player-controls-16';
import bowser from 'bowser';

import playerFullScreen from 'ck-core-client/graphics/player/player-fullscreen.svg';
import playerHide from 'ck-core-client/graphics/player/player-hide-chevron.svg';
import playerNext from 'ck-core-client/graphics/player/player-next.svg';
import playerPause from 'ck-core-client/graphics/player/player-pause.svg';
import playerPlay from 'ck-core-client/graphics/player/player-play.svg';
import playerPrev from 'ck-core-client/graphics/player/player-prev.svg';
import playerRewind from 'ck-core-client/graphics/player/player-rewind.svg';
import playerVolume from 'ck-core-client/graphics/player/player-volume.svg';
import closeButton from 'ck-core-client/graphics/close-button.svg';


const LOADING_VIDEO_FAILED = 'loadingVideoFailed',
	LOADING_TIMEOUT = 'loadingTimeout';

const fullScreenController = videoWrapper => {
	// Check for fullscreen support
	if (
		document.fullscreenElement ||
		document.mozFullScreenElement ||
		document.webkitFullscreenElement ||
		document.msFullscreenElement
	) {
		// If there's currently an element fullscreen, exit fullscreen
		if (document.exitFullscreen) {
			document.exitFullscreen();
		} else if (document.mozCancelFullScreen) {
			document.mozCancelFullScreen();
		} else if (document.webkitExitFullscreen) {
			document.webkitExitFullscreen();
		} else if (document.msExitFullscreen) {
			document.msExitFullscreen();
		}
	} else {
		// Otherwise, enter fullscreen
		// `videoWrapper` is just a `div` element wrapping the video
		if (videoWrapper) {
			if (videoWrapper.requestFullscreen) {
				videoWrapper.requestFullscreen();
			} else if (videoWrapper.mozRequestFullScreen) {
				videoWrapper.mozRequestFullScreen();
			} else if (videoWrapper.webkitRequestFullscreen) {
				videoWrapper.webkitRequestFullscreen();
			} else if (videoWrapper.msRequestFullscreen) {
				videoWrapper.msRequestFullscreen();
			}
		}
	}
};

const hasFullScreenApi = () => {
	return (
		document.exitFullscreen ||
		document.mozCancelFullScreen ||
		document.webkitExitFullscreen ||
		document.msExitFullscreen
	);
};

export default class VideoPlayer extends React.Component {
	constructor(props) {
		super(props);

		this._seconds = 0;

		this._id = Math.random();

		const StorageManager = require('../managers/StorageManager');

		this.state = {
			duration: 1,
			currentTime: this.props.secondsOffset || 0,
			currentSeek: 0,
			volume: +StorageManager.mod().get('videoVolume') || 50,
			isPaused: true,
			isMuted: StorageManager.mod().getBoolean('videoMuted') || false,
			fullscreen: props.fullscreen,
		};
		this.videoAndControlsWrapper = React.createRef();
	}
	componentDidMount() {
		if (hasFullScreenApi) {
			const onFullScreenChange = () => {
				const fullScreenElement =
					document.fullscreenElement ||
					document.msFullscreenElement ||
					document.mozFullScreenElement ||
					document.webkitFullscreenElement;

				this.setState(() => {
					this.props.onFullscreenChange && this.props.onFullscreenChange(!!fullScreenElement);
					return { fullscreen: !!fullScreenElement };
				});
			};
			// Do onFullScreenChange when the video enters fullscreen mode
			if (document.onfullscreenchange === null) document.onfullscreenchange = onFullScreenChange;
			else if (document.onmsfullscreenchange === null)
				document.onmsfullscreenchange = onFullScreenChange;
			else if (document.onmozfullscreenchange === null)
				document.onmozfullscreenchange = onFullScreenChange;
			else if (document.onwebkitfullscreenchange === null)
				document.onwebkitfullscreenchange = onFullScreenChange;
		}
	}
	componentWillUnmount() {
		if (this.player) this.player.unload();
	}

	componentWillReceiveProps(nextProps) {
		if (nextProps.videoId !== this.props.videoId) this.setState({ error: null });
	}
	async setCurrentTime(currentTime) {
		this.setState({ currentTime });
		await this.player.setCurrentTime(currentTime);
	}

	async setVideo(videoId) {
		if (this._videoId === videoId) return;

		this._videoId = videoId;

		const TIMEOUT_MS = 60000;

		try {
			await Promise.resolve(this.player.loadVideo(videoId)).timeout(TIMEOUT_MS);

			this.setState({ duration: await this.player.getDuration() });

			CK.logger.debug('Vimeo Player loaded', { videoId });

			if (this.props.autoplay) {
				if (this.props.secondsOffset) {
					await this.setCurrentTime(this.props.secondsOffset);
				}

				await this.player.play();
			}
		} catch (err) {
			if (err instanceof Promise.TimeoutError) {
				CK.logger.warn('Vimeo loadVideo timeout', {
					videoId,
					timeoutMs: TIMEOUT_MS
				});

				try {
					const actualVideoId = await Promise.resolve(this.player.getVideoId()).timeout(5000);

					CK.logger.warn(
						'CORE-2100: Vimeo loadVideo timeout but video ID was returned from the player',
						{
							videoId,
							actualVideoId,
							timeoutMs: TIMEOUT_MS
						}
					);
				} catch (err) {
					this.setState({ error: LOADING_TIMEOUT });
				}
			} else {
				this.setState({ error: LOADING_VIDEO_FAILED });

				this.setState({ duration: await this.player.getDuration() });

				CK.logger.error('Vimeo loadVideo error', { err, videoId });
			}
		}
	}

	setupPlayer(videoId, videoWrapper, autoPlay, playInNativeMobile) {
		if (typeof window === 'undefined' || !videoWrapper) return;

		this.setState({ error: null });

		this._videoElement = videoWrapper;

		const player = (this.player = new Vimeo(videoWrapper, {
			autoplay: !!autoPlay,
			id: videoId,
			quality: '720p',
			playsinline: true //!playInNativeMobile // playsinline is actually false to go fullscreen in mobile browser
		}));

		// Make autoplay work on mobile
		if (autoPlay) this.player.play();

		player.on('timeupdate', this.handleTimeUpdate.bind(this));

		player.on('play', () => {
			this.setState({ isPaused: false });
			this.player.setCurrentTime(this.state.currentTime);
		});

		player.on('pause', () => {
			this.setState({ isPaused: true });
		});

		player.on('error', error => {
			CK.logger.error('Vimeo Player error', { error });
			this.setState({ error });
		});

		player.setVolume(this.state.isMuted ? 0 : this.state.volume / 100);
	}

	handleTimeUpdate(timeData) {
		const seconds = Math.floor(timeData.seconds);

		if (seconds === this._seconds) return;

		this.setState({
			duration: timeData.duration,
			currentTime: seconds
		});

		if (this.props.handleTimeUpdate) this.props.handleTimeUpdate(seconds);

		this._seconds = seconds;

		if (this.props.cues) {
			for (const cue of this.props.cues) {
				const pastCueTime = cue.lookAhead && !cue.done && this._seconds > cue.time;
				const atCueTime = cue.time === this._seconds;
				if (pastCueTime || atCueTime) {
					this.props.handleCue(cue);
					cue.done = true;
				}
			}
		}
	}

	pause() {
		if (this.player) this.player.pause();
	}
	snapSeekToTime(seekTime) {
		const THRESHOLD = 30;
		for (let chapterDot of this.props.chapterDots) {
			if (Math.abs(chapterDot.secondsOffset - seekTime) < THRESHOLD) {
				return chapterDot.secondsOffset;
			}
		}

		return seekTime;
	}
	renderErrorBlanker(error) {
		if (error !== LOADING_VIDEO_FAILED) {
			CK.logger.error('Unknown VideoPlayer error', {
				errorCode: error
			});
		}

		return (
			<div className="VideoPlayer__errorBlanker">
				Having issues playing the video? <a href="https://faq.codekingdoms.com/en/articles/3738846-videos-aren-t-working-for-me">
					Read this article for help.
				</a>
				<img src={closeButton} onClick={() => 
					this.setState({
						error: null
					})
				}/>
			</div>
		);
	}
	renderControls(useNativeMobile) {
		return (
			<div className="VideoPlayer__controls">
				<ProgressBar
					isEnabled={true}
					totalTime={this.state.duration}
					currentTime={this.state.currentSeek || this.state.currentTime}
					isSeekable={true}
					onSeek={seekTime => {
						this.setState({ currentSeek: this.snapSeekToTime(seekTime) });
					}}
					onSeekStart={_.noop}
					onSeekEnd={seekTime => {
						this.setCurrentTime(this.snapSeekToTime(seekTime));
						this.setState({ currentSeek: null });
					}}
					onIntent={_.noop}
				/>
				{this.props.chapterDots && (
					<div className="VideoPlayer__controls__chapters">
						{this.props.chapterDots.map(chapterDot => (
							<div
								key={chapterDot.id}
								className={classnames(
									'VideoPlayer__controls__chapters__chapterDot',
									chapterDot.completed && 'VideoPlayer__controls__chapters__chapterDot__completed'
								)}
								style={{
									left: Math.round((chapterDot.secondsOffset / this.state.duration) * 100) + '%'
								}}
							/>
						))}
					</div>
				)}
				<div className="VideoPlayer__controls--flex">
					<div className="VideoPlayer__controls__playback">
						<img
							src={this.state.isPaused ? playerPlay : playerPause}
							className="VideoPlayer__controls__icon"
							style={{ height: '2rem' }}
							onClick={() => {
								this.state.isPaused ? this.player.play() : this.player.pause();
							}}
						/>
						<img
							src={playerRewind}
							className="VideoPlayer__controls__icon"
							style={{ width: '2rem', height: '2rem' }}
							onClick={() => {
								const currentTime = Math.max(0, this.state.currentTime - 10);
								this.setCurrentTime(currentTime);
							}}
						/>
						{this.props.cues && (
							<img
								src={playerPrev}
								className="VideoPlayer__controls__icon"
								style={{ width: '2rem', height: '2rem' }}
								onClick={() => {
									const TIME_OFFSET = 5;

									const currentTime = this.state.currentTime - TIME_OFFSET * 2;

									// Find previous chapter
									let recentTime = 0;
									for (let cue of this.props.cues) {
										if (cue.time >= currentTime) {
											break;
										}

										if (cue.name === 'pause') {
											recentTime = Math.max(recentTime, cue.time);
										}
									}

									this.setCurrentTime(recentTime + TIME_OFFSET);
								}}
							/>
						)}
						{this.props.cues && (
							<img
								src={playerNext}
								className="VideoPlayer__controls__icon"
								style={{ width: '2rem', height: '2rem' }}
								onClick={() => {
									const TIME_OFFSET = 5;

									const currentTime = this.state.currentTime;

									// Find previous chapter
									let recentTime = 0;
									for (let cue of this.props.cues) {
										if (cue.time < currentTime) {
											continue;
										}

										if (cue.name === 'pause') {
											recentTime = cue.time;
											break;
										}
									}

									this.setCurrentTime(recentTime + TIME_OFFSET);
								}}
							/>
						)}
						{!useNativeMobile && (
							<div className="VideoPlayer__controls__volume">
								<img
									src={playerVolume}
									className={`VideoPlayer__controls__icon ${
										this.state.isMuted ? 'VideoPlayer__controls__icon--muted' : ''
									}`}
									style={{ width: '2rem', height: '2rem' }}
									onClick={() => {
										const videoMuted = !this.state.isMuted;

										this.setState({ isMuted: videoMuted });
										this.player.setVolume(videoMuted ? 0 : this.state.volume / 100);

										const StorageManager = require('../managers/StorageManager');
										StorageManager.mod().setBoolean('videoMuted', videoMuted);
									}}
								/>
								<ProgressBar
									isEnabled={true}
									totalTime={100}
									currentTime={this.state.isMuted ? 0 : this.state.volume}
									isSeekable={true}
									onSeek={volume => {
										this.setState({ volume, isMuted: false });

										const StorageManager = require('../managers/StorageManager');
										StorageManager.mod().set('videoVolume', volume);

										this.player.setVolume(volume / 100);
									}}
									onIntent={_.noop}
								/>
							</div>
						)}
					</div>
					<div className="VideoPlayer__controls__right">
						{this.state.duration > 1 && (
							<TimeMarker
								totalTime={this.state.duration}
								currentTime={this.state.currentTime}
								markerSeparator=" / "
							/>
						)}
						{!useNativeMobile && !this.props.hideFullScreenButton && (
							<img
								src={
									playerFullScreen // used for video modal on ipad which doesn't have fullscreen support
								}
								className="VideoPlayer__controls__icon"
								style={{ width: '2rem', height: '2rem' }}
								onClick={() => {
									hasFullScreenApi()
										? fullScreenController(this.videoAndControlsWrapper)
										: this.setState(prevState => {
												// ios has to use the css fall back
												this.props.onFullscreenChange &&
													this.props.onFullscreenChange(!prevState.fullscreen);
												return { fullscreen: !prevState.fullscreen };
										  });
								}}
							/>
						)}
						{!this.state.fullscreen && this.props.enableHideButton && (
							<><img
								src={playerHide}
								className="VideoPlayer__controls__icon"
								style={{ height: '2rem' }}
								onClick={() => {
									if (this.props.isExpanded) {
										this.props.handleExpand(false);
									} else {
										this.props.handleHide();
										this.player.pause();
									}
								}}
							/>
							{!this.props.isExpanded && this.props.handleExpand &&
							<img
								src={playerHide}
								className="VideoPlayer__controls__icon flipped"
								style={{ height: '2rem' }}
								onClick={() => {
									this.props.handleExpand(true);
								}}
							/>}</>
						)}
					</div>
				</div>
			</div>
		);
	}

	render() {
		const { videoName, autoplay = false, playInNativeMobile = true } = this.props;
		const { error } = this.state;
		const useNativeMobile = bowser.mobile && playInNativeMobile;
		return (
			<div
				className={
					classnames(
						'VideoPlayer',
						CK.mobile ? 'mobile' : null,
						this.state.fullscreen ? 'fullscreen' : null
					) // mobile and tablets
				}
			>
				{error && this.renderErrorBlanker(error)}
				<div
					className="VideoPlayer__video"
					ref={parent => {
						this.videoAndControlsWrapper = parent;
						let videoId = this.props.videoId;
						if (!videoId) {
							const videos = require('ck-core/source/collections/videos');
							videoId = videos(videoName).url();
						}
						if (this.player) {
							this.setVideo(videoId);
						} else {
							this.setupPlayer(videoId, parent, autoplay, useNativeMobile);
						}
					}}
				>
					{this.renderControls(useNativeMobile)}
				</div>
			</div>
		);
	}
}

VideoPlayer.defaultProps = {
	fullscreen: false,
	enableHideButton: false,
	chapterDots: []
};

VideoPlayer.propTypes = {
	enableHideButton: PropTypes.bool,
	isExpanded: PropTypes.bool,
	fullscreen: PropTypes.bool,
	videoId: PropTypes.string,
	videoName: PropTypes.string,
	autoplay: PropTypes.bool,
	playInNativeMobile: PropTypes.bool,
	hideFullScreenButton: PropTypes.bool,
	secondsOffset: PropTypes.number,
	handleHide: PropTypes.func,
	handleExpand: PropTypes.func,
	chapterDots: PropTypes.array,
	cues: PropTypes.array,
	handleCue: PropTypes.func,
	handleTimeUpdate: PropTypes.func,
	onFullscreenChange: PropTypes.func
};
